import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import dayjs from 'dayjs'
import { logOutApp, store } from 'src/app/store'
import { logIn } from 'src/app/store/auth'
import { IDefaultResponse } from '../models/auth.model'
import { renewToken } from '../services/AuthenticationService'
import { lcStorageUserRole } from '../constants/localStorage.constants'

export const handleTokenExpired = async (axiosInstance: AxiosInstance, error: AxiosError<IDefaultResponse<any>>) => {
  const originalRequest = error.config as AxiosError<IDefaultResponse<any>>['config'] & { _retry?: boolean }
  const statusCode = error.response?.status

  if (statusCode === 401 && !originalRequest?._retry) {
    originalRequest._retry = true

    try {
      // Attempt to refresh the token
      const localRefreshToken = store.getState().auth?.refreshToken
      if (localRefreshToken) {
        const response = await renewToken(localRefreshToken)
        const newAccessToken = response?.data?.accessToken
        if (newAccessToken) {
          store.dispatch(
            logIn({
              ...response.data,
              accessTokenExpiresAt: dayjs().add(response.data.accessExpiresIn, 'seconds').format(),
            }),
          )
          // Update the Authorization header
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`

          // Retry the original request
          return axiosInstance(originalRequest)
        }
      }
    } catch (error) {
      // Handle token refresh failure (e.g., log out the user)
      sessionStorage.removeItem('persist:root')
      sessionStorage.removeItem(lcStorageUserRole)
      store.dispatch(logOutApp())
      return undefined
    }
  }
  return undefined
}

export const handleGetAccessToken = async (
  request: InternalAxiosRequestConfig<any>,
): Promise<InternalAxiosRequestConfig<any>> => {
  const authenState = store.getState().auth
  if (authenState?.accessToken && authenState?.accessTokenExpiresAt) {
    // check if token is expired
    const isTokenExpired = dayjs().isAfter(dayjs(authenState.accessTokenExpiresAt))
    console.log('IS TOKEN EXPIRED: ', isTokenExpired, dayjs(authenState.accessTokenExpiresAt).diff(dayjs(), 'minutes'))
    if (isTokenExpired) {
      // refresh token
      const refreshToken = authenState.refreshToken
      try {
        const response = await renewToken(refreshToken)
        const newAccessToken = response?.data?.accessToken
        if (newAccessToken) {
          store.dispatch(
            logIn({
              ...response.data,
              accessTokenExpiresAt: dayjs().add(response.data.accessExpiresIn, 'seconds').format(),
            }),
          )
          // Update the Authorization header
          request.headers['Authorization'] = `Bearer ${newAccessToken}`
          return Promise.resolve(request)
        }
      } catch (error) {
        console.log('ERROR WHILE REQUESTING: ', error)
        return Promise.resolve(request)
      }
    } else {
      request.headers['Authorization'] = `Bearer ${authenState.accessToken}`
      return Promise.resolve(request)
    }
  } else {
    const authenticationHeader = request.headers['Authorization'];
    const persistentBearerToken = (authenticationHeader as string);
    if (persistentBearerToken?.length) {
      request.headers.delete('Authorization');
    }
  }
  return Promise.resolve(request)
}
